import { IEmployeeAppPinnedItem } from '@dadohr/shared/domainTypes/experience';
import { Button, CircularProgress, IconButton } from '@material-ui/core';
import { Pin } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import {
  useIsItemPinned,
  usePinItemsMutation
} from '../../../services/pinnedItems';

const usePinItemCallback = (
  userAccessToken: string,
  type: IEmployeeAppPinnedItem['type'],
  templateId: IEmployeeAppPinnedItem['templateId'],
  itemId: IEmployeeAppPinnedItem['itemId']
): [(nextPinned: boolean) => void, boolean] => {
  const [mutationInProgress, setMutationInProgress] = useState(false);
  const pinItem = usePinItemsMutation(userAccessToken);
  const onPin = useCallback(
    (nextPinned: boolean) => {
      setMutationInProgress(true);
      pinItem({ type, templateId, itemId }, nextPinned).finally(() => {
        setMutationInProgress(false);
      });
    },
    [pinItem, type, templateId, itemId]
  );
  return [onPin, mutationInProgress];
};

const PinIcon = ({ isPinned, size }: { isPinned: boolean; size: number }) => {
  return (
    <Pin
      size={size}
      style={{
        fill: isPinned ? 'currentColor' : undefined
      }}
    />
  );
};

export const PinIconButton = ({
  userAccessToken,
  type,
  templateId,
  itemId
}: {
  userAccessToken: string;
  type: IEmployeeAppPinnedItem['type'];
  templateId: IEmployeeAppPinnedItem['templateId'];
  itemId: IEmployeeAppPinnedItem['itemId'];
}) => {
  const [isPinned, loading] = useIsItemPinned(
    userAccessToken,
    type,
    templateId,
    itemId
  );
  const [onPin, mutationInProgress] = usePinItemCallback(
    userAccessToken,
    type,
    templateId,
    itemId
  );
  // const { t } = useUnsafeTranslation();
  // const tooltipTitle = loading
  //   ? ''
  //   : mutationInProgress
  //   ? t('Updating...')
  //   : isPinned
  //   ? t('Unpin')
  //   : t('Pin');
  return (
    // The tooltip is caused a cannot update unmounted component error - strangely.
    // <Tooltip title={tooltipTitle}>
    <IconButton
      onClick={(ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        onPin(!isPinned);
      }}
    >
      {mutationInProgress || loading ? (
        <CircularProgress size={14} />
      ) : (
        <PinIcon isPinned={!!isPinned} size={14} />
      )}
    </IconButton>
    // </Tooltip>
  );
};

export const PinButton = ({
  userAccessToken,
  type,
  templateId,
  itemId,
  label
}: {
  userAccessToken: string;
  type: IEmployeeAppPinnedItem['type'];
  templateId: IEmployeeAppPinnedItem['templateId'];
  itemId: IEmployeeAppPinnedItem['itemId'];
  label?: (isPinned: boolean) => string;
}) => {
  const [isPinned, loading] = useIsItemPinned(
    userAccessToken,
    type,
    templateId,
    itemId
  );
  const [onPin, mutationInProgress] = usePinItemCallback(
    userAccessToken,
    type,
    templateId,
    itemId
  );
  const { t } = useUnsafeTranslation();
  return (
    <Button
      disabled={loading}
      onClick={() => onPin(!isPinned)}
      startIcon={
        mutationInProgress ? (
          <CircularProgress size={14} />
        ) : (
          <PinIcon isPinned={!!isPinned} size={14} />
        )
      }
      size="small"
      variant="outlined"
    >
      {!loading && label && label(!!isPinned)}
      {!loading &&
        !label &&
        (!!isPinned
          ? t('Unpin')
          : t(type === 'TASK' ? 'Pin this task' : 'Pin this message'))}
    </Button>
  );
};
